import { useState } from "react";

import type { AgenticLogicOnEmpty } from "@intelligems/ig-types";
import { IgTypography } from "@intelligems/ig-theme";
import { Box, Skeleton, Stack, useTheme } from "@mui/material";

import { RULE_GRAPH_RESULT_WIDTH } from "./layout";
import {
  formatScenarioContext,
  type RuleGraphScenarioContext,
} from "./scenario-context";

export interface RuleGraphShopperOutputProduct {
  id: string;
  title: string;
  /** The variant's own title, when the row is one colour rather than a product. */
  variantTitle?: string | null;
  price: string;
  imageUrl?: string | null;
}

export interface RuleGraphShopperOutputProps {
  products: RuleGraphShopperOutputProduct[];
  explanation: string;
  empty: boolean;
  /** The rule's empty-result policy — what the empty face says the shopper sees. */
  onEmpty: AgenticLogicOnEmpty;
  /**
   * The request the run was judged on. Rendered as one caption line under
   * the header ("Checkout · Local pickup"), so a delivery-keyed rule's answer
   * says which delivery it is the answer for.
   */
  scenarioContext?: RuleGraphScenarioContext;
}

const MAX_VISIBLE_PRODUCTS = 3;

const ProductThumbnail = ({
  product,
}: {
  product: RuleGraphShopperOutputProduct;
}) => {
  const theme = useTheme();
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);
  const hasImage =
    Boolean(product.imageUrl) && product.imageUrl !== failedImageUrl;

  if (!hasImage || !product.imageUrl) {
    // A skeleton square, not a coloured tile. A product with no picture — or
    // whose picture 404s — is a cosmetic gap, and painting it in the error
    // background put an alarm-red chip beside every row of a panel that is
    // often already reporting real failures. The pulse is off deliberately:
    // nothing is loading, so an animation would promise an image that is
    // never coming.
    return (
      <Skeleton
        animation={false}
        aria-label={`No image for ${product.title}`}
        data-testid={`shopper-output-image-placeholder-${product.id}`}
        height="28px"
        sx={{
          backgroundColor: theme.igBackgroundColors.baseLightLow,
          borderRadius: "4px",
          flexShrink: 0,
        }}
        variant="rounded"
        width="28px"
      />
    );
  }

  return (
    <Box
      alt={product.title}
      component="img"
      onError={() => setFailedImageUrl(product.imageUrl ?? null)}
      src={product.imageUrl}
      sx={{
        borderRadius: "4px",
        flexShrink: 0,
        height: "28px",
        objectFit: "cover",
        width: "28px",
      }}
    />
  );
};

export const RuleGraphShopperOutput = ({
  products,
  explanation,
  empty,
  onEmpty,
  scenarioContext,
}: RuleGraphShopperOutputProps) => {
  const theme = useTheme();
  const visibleProducts = products.slice(0, MAX_VISIBLE_PRODUCTS);
  const hiddenProductCount = Math.max(
    products.length - MAX_VISIBLE_PRODUCTS,
    0
  );

  return (
    <Stack
      sx={{
        backgroundColor: theme.igBackgroundColors.baseLightHigh,
        border: `1px solid ${theme.igBorders.core.default}`,
        borderRadius: "8px",
        boxShadow: theme.igShadows.low,
        boxSizing: "border-box",
        // The panel sits in a flex column sized to the card row it centers
        // on, and it is taller than that row: without this it would be
        // compressed to the row's height and lose its explanation line.
        flexShrink: 0,
        gap: empty ? "8px" : "10px",
        overflow: "hidden",
        padding: empty ? "10px 12px 12px" : "12px 14px",
        width: `${RULE_GRAPH_RESULT_WIDTH}px`,
      }}
    >
      <IgTypography
        component="p"
        sx={{
          color: theme.palette.text.placeholder,
          fontSize: "10px",
          fontWeight: 600,
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        WHAT THE SHOPPER SEES
      </IgTypography>
      {scenarioContext ? (
        <IgTypography
          component="p"
          data-testid="shopper-output-scenario-context"
          noWrap
          sx={{
            color: theme.palette.text.tertiary,
            fontSize: "10px",
            lineHeight: 1.5,
            margin: 0,
          }}
        >
          {formatScenarioContext(scenarioContext)}
        </IgTypography>
      ) : null}
      {empty ? (
        <>
          <Stack
            alignItems="center"
            data-testid="shopper-output-empty-state"
            justifyContent="center"
            sx={{
              border: `1px dashed ${theme.igBorders.core.default}`,
              borderRadius: "6px",
              padding: "10px",
            }}
          >
            <IgTypography
              component="p"
              sx={{
                color: theme.palette.text.tertiary,
                fontSize: "12px",
                lineHeight: 1.5,
                margin: 0,
                textAlign: "center",
              }}
            >
              {onEmpty === "smart-recs"
                ? "Smart recommendations — the rule's own picks came up empty."
                : "Nothing — the upsell is hidden."}
            </IgTypography>
          </Stack>
          <Explanation text={explanation} />
        </>
      ) : (
        <>
          {visibleProducts.map((product) => (
            <Stack
              alignItems="center"
              data-testid="shopper-output-product"
              direction="row"
              gap="10px"
              key={product.id}
              minWidth={0}
            >
              <ProductThumbnail product={product} />
              <Stack gap="2px" minWidth={0} overflow="hidden">
                <IgTypography
                  component="p"
                  noWrap
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: "10px",
                    lineHeight: 1.5,
                    margin: 0,
                  }}
                >
                  {product.title}
                </IgTypography>
                {product.variantTitle ? (
                  // One card per colour is only readable if the colour is on it.
                  <IgTypography
                    component="p"
                    noWrap
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "10px",
                      lineHeight: 1.5,
                      margin: 0,
                    }}
                  >
                    {product.variantTitle}
                  </IgTypography>
                ) : null}
                <IgTypography
                  component="p"
                  sx={{
                    color: theme.palette.text.tertiary,
                    fontSize: "10px",
                    lineHeight: 1.5,
                    margin: 0,
                  }}
                >
                  {product.price}
                </IgTypography>
              </Stack>
            </Stack>
          ))}
          {hiddenProductCount > 0 ? (
            <IgTypography
              component="p"
              sx={{
                color: theme.palette.text.tertiary,
                fontSize: "10px",
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              +{hiddenProductCount} more
            </IgTypography>
          ) : null}
          <Box
            data-testid="shopper-output-divider"
            sx={{
              borderTop: `1px solid ${theme.igBorders.core.default}`,
              height: 0,
              width: "100%",
            }}
          />
          <Explanation text={explanation} />
        </>
      )}
    </Stack>
  );
};

const Explanation = ({ text }: { text: string }) => {
  const theme = useTheme();

  return (
    <IgTypography
      component="p"
      sx={{
        color: theme.palette.text.tertiary,
        fontSize: "10px",
        lineHeight: 1.5,
        margin: 0,
      }}
    >
      {text}
    </IgTypography>
  );
};
